import type { MailThreadListItem } from '../thread-view/thread-types';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/**
 * @notice Default mailbox split shown for an account route.
 */
export const DEFAULT_MAIL_SPLIT_ID: MailSplitId = 'important';

/**
 * @notice Ordered mailbox split metadata used by mailbox navigation.
 */
export const MAIL_SPLITS: Array<{ splitId: MailSplitId; label: string }> = [
  { splitId: 'important', label: 'Important' },
  { splitId: 'other', label: 'Other' },
  { splitId: 'starred', label: 'Starred' },
  { splitId: 'sent', label: 'Sent mail' },
  { splitId: 'drafts', label: 'Drafts' },
  { splitId: 'done', label: 'Archived' },
  { splitId: 'all', label: 'All mail' },
  { splitId: 'spam', label: 'Spam' },
  { splitId: 'trash', label: 'Trash' },
];

/**
 * @notice Ordered inbox-only split metadata used by the primary inbox tabs.
 */
export const MAIL_INBOX_SPLITS: Array<{ splitId: MailSplitId; label: string }> = [
  { splitId: 'important', label: 'Important' },
  { splitId: 'other', label: 'Other' },
];

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type MailSplitId =
  | 'important'
  | 'other'
  | 'starred'
  | 'sent'
  | 'drafts'
  | 'done'
  | 'all'
  | 'spam'
  | 'trash';

export interface MailSplitSummary {
  splitId: MailSplitId;
  label: string;
  total: number;
  unread: number;
}

export interface MailThreadListSnapshot {
  accountId?: string;
  after?: string;
  anchorLeadingCount?: number;
  /** Absolute zero-based start of this contiguous window at threadListRevision. */
  windowStart?: number | null;
  rebuilding?: boolean;
  threadListRevision?: number;
  splitId: MailSplitId;
  limit: number;
  nextCursor?: string;
  total: number;
  hasMore: boolean;
  remoteHasMore?: boolean;
  threads: MailThreadListItem[];
  splitSummaries: MailSplitSummary[];
}

export type MailThreadListStaleWindow = {
  staleWindow: true;
  threadListRevision: number;
};

export type MailThreadListResult = MailThreadListSnapshot | MailThreadListStaleWindow;
